import React from 'react'
import type { AnyAction, Dispatch } from 'redux'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { API } from '../../../api'
import { startLoading, stopLoading } from '../../../modules/loading/actions'
import { LoadingTypes } from '../../../modules/loading/reducer'
import { updateAppAlert } from '../../../modules/appAlert/actions'
import { marketplaceServiceUrl } from '../../../utils/constants'
import { getErrorMessages } from '../../../utils/error'
import { errorLogger, type LoggerGlobalState } from '../../../utils/errorLogger'
import { PatchMilestonesDataFailureMessage } from '../../../utils/messagesContants'
import type { ThunkDispatch } from 'redux-thunk'

export type MilestonesDispatch = ThunkDispatch<
  LoggerGlobalState,
  unknown,
  AnyAction
>

const getMilestonesData = (partnerId: string) =>
  API.get(`${marketplaceServiceUrl}/v1/partners/${partnerId}`, {
    params: {
      seller_milestones: true,
    },
  })

const patchMilestonesData = (
  partnerId: string,
  data: Record<string, unknown>
) =>
  API.patch(`${marketplaceServiceUrl}/v1/partners/${partnerId}`, data, {
    params: {
      operations: 'update_seller_milestones',
      cloud_marketplace: 'aws',
    },
  })

export const getMilestonesApiData =
  (
    partnerId: string,
    setSaasProductsData: React.Dispatch<React.SetStateAction<unknown>>
  ) =>
  async (_dispatch: Dispatch<AnyAction>, getState: () => LoggerGlobalState) => {
    try {
      const response = await getMilestonesData(partnerId)
      const saasProducts = (
        response?.data as { saas_products?: unknown } | undefined
      )?.saas_products
      if (saasProducts) {
        await setSaasProductsData(camelize(saasProducts))
      }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }

export const patchMilestonesApiData =
  (partnerId: string, keyName: string) =>
  async (dispatch: Dispatch<AnyAction>, getState: () => LoggerGlobalState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await patchMilestonesData(
        partnerId,
        snakeize({
          saasProducts: {
            milestones: {
              [keyName]: true,
            },
          },
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([PatchMilestonesDataFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
